import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button } from '@/components/ui/daisy/button';

import { HOURS_OPTIONS, MINUTES_OPTIONS } from './constants';
import {
  formatTimeValue,
  isHourDisabled,
  isMinuteDisabled,
  padTime,
  parseTimeValue,
} from './timeUtils';
import { ParsedTime, TimePickerProps } from './types';

const columnClasses =
  'flex h-40 flex-col gap-0.5 overflow-y-auto scrollbar-thin scrollbar-thumb-primary/40 scrollbar-thumb-rounded-full';

type TimeOptionProps = {
  value: number;
  selected: boolean;
  disabled: boolean;
  onSelect: (value: number) => void;
};

const TimeOption = ({
  value,
  selected,
  disabled,
  onSelect,
}: TimeOptionProps) => (
  <Button
    type="button"
    role="option"
    aria-selected={selected}
    aria-disabled={disabled}
    disabled={disabled}
    active={selected}
    variant="ghost"
    size="sm"
    className="px-3 font-normal"
    onClick={() => onSelect(value)}
  >
    {padTime(value)}
  </Button>
);

export const TimePicker = ({
  value,
  onChange,
  blockedTimes,
  disabled = false,
  hoursAriaLabel,
  minutesAriaLabel,
  className,
}: TimePickerProps) => {
  const [selection, setSelection] = useState<ParsedTime>(() =>
    parseTimeValue(value),
  );

  useEffect(() => {
    setSelection(parseTimeValue(value));
  }, [value]);

  const handleSelect = (partial: ParsedTime) => {
    const { hours, minutes } = { ...selection, ...partial };
    setSelection({ hours, minutes });

    if (hours === undefined || minutes === undefined) return;
    onChange(formatTimeValue(hours, minutes));
  };

  return (
    <div className={twMerge('flex gap-2', className)}>
      <div role="listbox" aria-label={hoursAriaLabel} className={columnClasses}>
        {HOURS_OPTIONS.map(hour => (
          <TimeOption
            key={hour}
            value={hour}
            selected={selection.hours === hour}
            disabled={
              disabled || isHourDisabled(hour, blockedTimes, selection.minutes)
            }
            onSelect={hours => handleSelect({ hours })}
          />
        ))}
      </div>
      <div
        role="listbox"
        aria-label={minutesAriaLabel}
        className={columnClasses}
      >
        {MINUTES_OPTIONS.map(minute => (
          <TimeOption
            key={minute}
            value={minute}
            selected={selection.minutes === minute}
            disabled={
              disabled ||
              isMinuteDisabled(minute, blockedTimes, selection.hours)
            }
            onSelect={minutes => handleSelect({ minutes })}
          />
        ))}
      </div>
    </div>
  );
};

TimePicker.displayName = 'TimeInput.Picker';
